import React from "react";
import { motion } from "framer-motion";
import { GraduationCap, BookOpenCheck, Award, HeartHandshake, Sparkles } from "lucide-react";

const CREDENTIALS = [
  { icon: Award, label: "20+ Years in Education" },
  { icon: GraduationCap, label: "Master's Degree in Education" },
  { icon: BookOpenCheck, label: "Certified Reading Specialist" },
  { icon: Sparkles, label: "Dyslexia Therapist" },
  { icon: HeartHandshake, label: "Early Literacy Support" },
];

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 grid lg:grid-cols-12 gap-12 items-start">
        {/* Photo / Card */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-5"
          data-testid="founder-bio-section"
        >
          <div className="relative rounded-[2.2rem] overflow-hidden bg-gradient-to-br from-wg-teal/15 via-wg-cream to-wg-yellow/20 p-5 pb-20 shadow-[0_18px_50px_-20px_rgba(74,29,82,0.25)]">
            <div className="aspect-[4/5] rounded-[1.6rem] overflow-hidden bg-wg-cream2">
              <img
                src="/laura-cummings.jpg"
                alt="Laura Cummings, founder and certified Reading Specialist & Dyslexia Therapist of Word Gumbo Learning Co."
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>

            {/* credential chip */}
            <div className="absolute left-6 right-6 bottom-5 bg-wg-purple text-white rounded-2xl px-5 py-4 shadow-xl">
              <div className="text-xs uppercase tracking-wider opacity-80">Founder</div>
              <div className="font-heading font-bold text-lg md:text-xl">Laura Cummings, M.Ed.</div>
            </div>
          </div>
        </motion.div>

        {/* Copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-3.5 py-1.5 border border-wg-purple/15">
              <span className="w-1.5 h-1.5 rounded-full bg-wg-orange" />
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-wg-purple">About</span>
            </div>
            <h2 className="mt-5 font-heading font-extrabold text-3xl md:text-5xl tracking-tight text-wg-ink leading-tight">
              Hi! I'm <span className="text-wg-purple">Laura Cummings</span>, founder of Word Gumbo Learning Co.
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 space-y-5 text-[17px] leading-relaxed text-wg-ink2"
          >
            <p>
              With over 20 years of experience in education, I'm passionate about helping children
              become confident, successful readers. I hold a Master's degree in Education and am a
              certified Reading Specialist and Dyslexia Therapist with specialized training in
              dyslexia intervention and early literacy development.
            </p>
            <p>
              My specialty is working with students from Kindergarten through 2nd grade, helping
              young learners build strong foundational reading skills during the most important
              years of literacy development. I also support struggling readers of all ages through
              personalized, evidence-based instruction tailored to each child's unique learning needs.
            </p>
            <p>
              Throughout my career, I have focused on creating engaging, confidence-building
              learning experiences that help children grow academically and emotionally. At Word
              Gumbo Learning Co., I believe every child can thrive with the right support,
              encouragement, and strategies.
            </p>
            <p>
              Whether through tutoring, intervention, dyslexia screeners, or comprehensive dyslexia
              testing, my goal is to help families better understand how their child learns while
              providing meaningful tools for long-term success.
            </p>
            <p className="text-wg-purple font-semibold font-heading text-xl">
              I look forward to partnering with your family and helping your child read, learn, grow, and succeed.
            </p>
          </motion.div>

          {/* Credential pills */}
          <motion.ul
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={{
              hidden: {},
              visible: { transition: { staggerChildren: 0.07 } },
            }}
            className="mt-8 flex flex-wrap gap-3"
            data-testid="credentials-list"
          >
            {CREDENTIALS.map(({ icon: Icon, label }) => (
              <motion.li
                key={label}
                variants={{
                  hidden: { opacity: 0, y: 12 },
                  visible: { opacity: 1, y: 0 },
                }}
                className="inline-flex items-center gap-2 bg-white border border-wg-purple/10 rounded-full pl-3 pr-5 py-2 shadow-sm hover:-translate-y-0.5 hover:shadow-md transition-all"
              >
                <motion.span
                  whileHover={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 0.5 }}
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-wg-purple/8 text-wg-purple"
                >
                  <Icon className="w-4 h-4" />
                </motion.span>
                <span className="text-sm font-semibold text-wg-ink">{label}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </div>
    </section>
  );
}
